import {
  IExtendedUploadConfiguration,
  IFileModel,
  InfoText,
  IUploadConfiguration,
  IUploadDeletePopupConfiguration,
  IUploadResult,
} from '@mwe/models';
import { ChangeDetectionStrategy, Component, computed, inject, input, OnDestroy, OnInit, output, signal, viewChild } from '@angular/core';
import { UploadComponent } from './upload.component';
import { MediaPreviewComponent } from '../media-preview/media-preview.component';
import { InfoTextPopupFromLinkDirective } from '../info-text/info-text-popup-from-link/info-text-popup-from-link.directive';
import { NgClass } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { InfoboxComponent } from '../infobox/infobox.component';
import { SafeHtmlPipe } from '@mwe/ui/pipes';
import { MemoryCacheItem, MemoryCacheService, PopupService } from '@mwe/services';

@Component({
  selector: 'mwe-extended-upload',
  templateUrl: './extended-upload.component.html',
  imports: [InfoTextPopupFromLinkDirective, NgClass, UploadComponent, TranslatePipe, SafeHtmlPipe, InfoboxComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExtendedUploadComponent implements OnInit, OnDestroy {
  private readonly popupService = inject(PopupService);
  private readonly memoryCacheService = inject(MemoryCacheService);

  readonly config = input<IExtendedUploadConfiguration>(undefined);
  readonly uploadResult = input<IUploadResult>(undefined);
  readonly emitUploadResult = output<IUploadResult>();

  readonly uploadComponent = viewChild<UploadComponent>('uploadComponent');

  showUploadError = signal<boolean>(false);
  showValidationError = signal<boolean>(false);
  uploadSuccessful = signal<boolean>(false);
  deletePopupConfiguration = signal<IUploadDeletePopupConfiguration>(undefined);
  uploadConfiguration = signal<IUploadConfiguration>(undefined);
  internalUploadResult = signal<IUploadResult>(undefined);

  uploadedFiles = computed<IFileModel[]>(() => this.internalUploadResult()?.uploadedFiles);
  isValid = computed<boolean>(() => this.internalUploadResult().valid);
  infoPopup = computed<InfoText[]>(() => [
    {
      code: 'documentInfoPopupOpener',
      title: 'upload.overlay.title',
      description: 'upload.overlay.description',
    },
  ]);
  hasError = computed<boolean>(() => this.showUploadError() || this.showValidationError());

  ngOnInit(): void {
    this.internalUploadResult.set(this.uploadResult());
    this.setUploadConfiguration();
    this.removeNotFullyLoadedFiles();
    this.checkForUploadErrors();
    this.fireFormValid();
  }

  ngOnDestroy(): void {
    this.removeNotFullyLoadedFiles();
  }

  setUploadConfiguration(): void {
    this.uploadConfiguration.set(this.config().uploadConfiguration);
    this.deletePopupConfiguration.set(this.config().deletePopupConfiguration);
  }

  setUploadedFiles(data: IUploadResult): void {
    if (data.valid) {
      data.uploadedFiles.forEach((fm: IFileModel) => {
        if (!fm.isLoading && fm.base64) {
          this.memoryCacheService.set(MemoryCacheItem.UploadDetails, fm.base64, fm.id);
          fm.base64 = null;
        }
      });
    }

    this.showValidationError.set(false);
    this.internalUploadResult.set(data);
    this.checkForUploadErrors();
    this.fireFormValid();
  }

  checkForUploadErrors(): void {
    this.showUploadError.set(false);
    if (!this.internalUploadResult()) return;

    this.internalUploadResult.update(res => {
      return {
        ...res,
        valid: this.uploadedFiles()?.length >= this.uploadConfiguration()?.minAmountOfFiles,
      };
    });

    for (let i = 0; i < this.uploadedFiles()?.length; i++) {
      if (this.uploadedFiles()?.[i]?.showError) {
        this.showUploadError.set(true);
        this.internalUploadResult.update(res => {
          const uploadedFiles = res.uploadedFiles.toSpliced(i, 1);
          return {
            ...res,
            uploadedFiles,
            valid: false,
          };
        });
        break;
      }
      if (this.uploadedFiles()[i].isLoading) {
        this.showUploadError.set(false);
        this.internalUploadResult.update(res => ({
          ...res,
          valid: false,
        }));
        break;
      }
    }
  }

  removeFile(fileToRemove: IFileModel): void {
    this.uploadComponent().uploadedFiles.set(this.internalUploadResult().uploadedFiles);
    this.uploadComponent().removeFile(fileToRemove);
  }

  previewFile(fileToShow: IFileModel): void {
    this.popupService.openV2({
      id: 'media-preview',
      titleKey: fileToShow.name,
      component: MediaPreviewComponent,
      componentData: { ...fileToShow, base64: this.memoryCacheService.get(MemoryCacheItem.UploadDetails, fileToShow.id) },
      dismissable: true,
      modalSize: 'lg',
      modalOpts: {
        ariaLabelledBy: 'mwe_info-text-popup-title',
      },
    });
  }

  fireFormValid(): void {
    this.emitUploadResult.emit(this.internalUploadResult());
  }

  validate(): boolean {
    if (!this.isValid()) {
      this.checkForUploadErrors();
      if (this.isValid()) {
        this.fireFormValid();
      } else {
        this.showValidationError.set(true);
      }
    }
    return this.isValid();
  }

  setUploadSuccessful(): void {
    this.uploadSuccessful.set(true);
  }

  private removeNotFullyLoadedFiles(): void {
    const valid = this.uploadedFiles()?.filter(file => file.showError).length > this.uploadConfiguration()?.minAmountOfFiles;
    const uploadedFiles = this.uploadedFiles()?.filter(file => !file.isLoading);
    this.setUploadedFiles({ uploadedFiles, valid });
  }
}
